import { Flame } from "lucide-react";
import { useEffect, useState } from "react";
import type { FengniaoDashboard } from "../hooks/useFengniaoDashboard";
import { formatBnb, formatToken } from "../lib/format";

interface BuybackPanelProps {
  dashboard: FengniaoDashboard;
}

function formatCountdown(delta: number): string {
  if (delta <= 0) return "可立即执行";
  const minutes = Math.floor(delta / 60);
  const seconds = String(delta % 60).padStart(2, "0");
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remMin = String(minutes % 60).padStart(2, "0");
    return `${hours}h ${remMin}m`;
  }
  return `${minutes}:${seconds}`;
}

export function BuybackPanel({ dashboard }: BuybackPanelProps) {
  const { buyback } = dashboard;
  const [now, setNow] = useState<number>(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(Math.floor(Date.now() / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  const delta = Number(buyback.nextExecutableAt - BigInt(now));
  const ready = delta <= 0;
  const countdownLabel = formatCountdown(delta);

  const progressPct = Math.min(100, buyback.pendingProgressBps / 100);

  return (
    <section className="glass p-5 lg:p-6">
      <header className="flex items-center gap-2.5 mb-2">
        <Flame className="w-5 h-5 text-ember" strokeWidth={1.75} aria-hidden />
        <h3 className="text-sm font-mono uppercase tracking-wider text-ink">回购销毁</h3>
      </header>
      <p className="text-xs text-ink-dim leading-relaxed mb-4">
        每笔交易的 10% 税用来回购蜂鸟代币然后销毁。流通量持续减少，长期利好持币人。
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <span
          className={`inline-flex items-center gap-1.5 rounded-chip border px-2 py-1 text-[10px] font-mono uppercase tracking-wider ${
            buyback.active
              ? "bg-leaf/15 text-leaf border-leaf/30"
              : "bg-white/[0.04] text-ink-dim border-white/10"
          }`}
        >
          <span
            className={`inline-block w-1.5 h-1.5 rounded-full ${buyback.active ? "bg-leaf animate-pulse" : "bg-ink-dim/40"}`}
          />
          {buyback.active ? "Active" : "Inactive"}
        </span>
        <span className="inline-flex items-center rounded-chip border border-white/10 bg-white/[0.02] px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-ink-dim">
          {buyback.pancakeOnly ? "Pancake only" : "Portal / Pancake"}
        </span>
      </div>

      <div className="mb-5">
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-[10px] font-mono uppercase tracking-wider text-ink-dim">
            Pending BNB
          </span>
          <span className="text-[10px] font-mono tabular-nums text-ink-dim">
            {progressPct.toFixed(1)}%
          </span>
        </div>
        <div className="text-2xl lg:text-3xl font-semibold font-mono tabular-nums text-ink leading-none">
          {formatBnb(buyback.pendingBnb)}
        </div>
        <div className="mt-3 h-1.5 rounded-chip bg-white/[0.04] overflow-hidden">
          <div
            className="h-full bg-bnb-grad transition-all duration-200"
            style={{ width: `${progressPct}%` }}
            role="progressbar"
            aria-valuenow={Math.round(progressPct)}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
        <div className="mt-1.5 text-[10px] font-mono text-ink-dim tabular-nums">
          已累积 / 触发阈值 {formatBnb(buyback.minBnb)}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-ink-dim">
            下次执行
          </div>
          <div
            className={`text-sm font-mono tabular-nums mt-1 ${ready ? "text-leaf" : "text-ink"}`}
          >
            {countdownLabel}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-ink-dim">
            累计 BNB
          </div>
          <div className="text-sm font-mono tabular-nums mt-1 text-ink break-all">
            {formatBnb(buyback.totalBnbUsed)}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-ink-dim">
            累计销毁
          </div>
          <div className="text-sm font-mono tabular-nums mt-1 text-ember break-all">
            {formatToken(buyback.totalTokensBurned, "蜂鸟")}
          </div>
        </div>
      </div>
    </section>
  );
}
